import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AppService } from 'src/app/app.service';
import { Router, ActivatedRoute } from '@angular/router';
import { EmployeeService } from '../employee.service';
import { UtilsService } from 'src/app/shared/utils.service';
import { MessageEditComponent } from 'src/app/messages/message-edit/message-edit.component';
import { RBACService } from 'src/app/shared/rbac.service';

@Component({
  selector: 'app-employees-tabs',
  templateUrl: './employees-tabs.component.html',
  styleUrls: ['./employees-tabs.component.scss'],
  providers: [EmployeeService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class EmployeesTabsComponent implements OnInit {
  // Permission flags resolved ONCE in ngOnInit() so the template does not
  // re-evaluate rbac.userCanAccess()/app.planCan() on every change detection cycle.
  canSendMessage: boolean = false;
  canEditEmployee: boolean = false;
  canViewOrEditEmployee: boolean = false;
  canViewEmployee: boolean = false;
  canAccessTrainingCompetencies: boolean = false;
  canManageLinkedSites: boolean = false;
  canViewAssignedForms: boolean = false;
  canViewNotifications: boolean = false;
  canViewRbacRoles: boolean = false;
  canViewSitesVisited: boolean = false;
  canViewSitesWorkedOn: boolean = false;

  constructor(
    public app: AppService,
    private route: ActivatedRoute,
    private router: Router,
    public employeeService: EmployeeService,
    public utils: UtilsService,
    public rbac: RBACService
  ) {}

  ngOnInit() {
    // Resolve all permission flags first so they are always set, even when an early return happens below.
    this.canSendMessage =
      this.app.planCan(AppService.FEATURES.MESSAGES) &&
      this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_MESSAGES,
            RBACService.RBAC_A.A_CREATE
          )
        ],
        true
      );

    this.canEditEmployee = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_EMPLOYEES,
          RBACService.RBAC_A.A_EDIT
        )
      ],
      true
    );

    this.canViewOrEditEmployee = this.rbac.userCanAccess([
      RBACService.getPermission(
        RBACService.RBAC_F.F_EMPLOYEES,
        RBACService.RBAC_A.A_VIEW
      ),
      RBACService.getPermission(
        RBACService.RBAC_F.F_EMPLOYEES,
        RBACService.RBAC_A.A_EDIT
      )
    ]);

    this.canViewEmployee = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_EMPLOYEES,
          RBACService.RBAC_A.A_VIEW
        )
      ],
      true
    );

    this.canAccessTrainingCompetencies = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_EMPLOYEES_TRAINING_COMPETENCIES,
          RBACService.RBAC_A.A_VIEW
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_EMPLOYEES_TRAINING_COMPETENCIES,
          RBACService.RBAC_A.A_CREATE
        )
      ],
      false
    );

    this.canManageLinkedSites =
      this.app.planCan(AppService.FEATURES.SITES) &&
      this.rbac.userCanAccess([
        RBACService.getPermission(
          RBACService.RBAC_F.F_EMPLOYEES_SITE_ASSOCIATIONS,
          RBACService.RBAC_A.A_VIEW
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_EMPLOYEES_SITE_ASSOCIATIONS,
          RBACService.RBAC_A.A_EDIT
        )
      ]);

    this.canViewAssignedForms =
      this.app.planCan(AppService.FEATURES.FORMS) &&
      this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_FORMS,
            RBACService.RBAC_A.A_VIEW
          )
        ],
        true
      );

    this.canViewNotifications =
      this.app.planCan(AppService.FEATURES.NOTIFICATION_ROLES) &&
      this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_ACCOUNT_NOTIFICATION_ROLES,
            RBACService.RBAC_A.A_VIEW
          )
        ],
        true
      );

    this.canViewRbacRoles = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_ACCOUNT_RBAC_ROLES,
          RBACService.RBAC_A.A_VIEW
        )
      ],
      true
    );

    this.canViewSitesVisited =
      this.app.planCan(AppService.FEATURES.SITE_ACCESS) &&
      this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_EMPLOYEES_SITE_ASSOCIATIONS,
            RBACService.RBAC_A.A_VIEW
          )
        ],
        true
      );

    this.canViewSitesWorkedOn =
      this.app.planCan(AppService.FEATURES.SITES) &&
      this.rbac.userCanAccess([
        RBACService.getPermission(
          RBACService.RBAC_F.F_EMPLOYEES_SITE_ASSOCIATIONS,
          RBACService.RBAC_A.A_VIEW
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_EMPLOYEES_SITE_ASSOCIATIONS,
          RBACService.RBAC_A.A_EDIT
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_EMPLOYEES_SITE_ASSOCIATIONS,
          RBACService.RBAC_A.A_DELETE
        )
      ]);

    // Page-level access check: redirect if user has NO F_EMPLOYEES permissions at all.
    const hasAnyPermission = this.rbac.userCanAccess([
      RBACService.getPermission(
        RBACService.RBAC_F.F_EMPLOYEES,
        RBACService.RBAC_A.A_VIEW
      ),
      RBACService.getPermission(
        RBACService.RBAC_F.F_EMPLOYEES,
        RBACService.RBAC_A.A_CREATE
      ),
      RBACService.getPermission(
        RBACService.RBAC_F.F_EMPLOYEES,
        RBACService.RBAC_A.A_EDIT
      ),
      RBACService.getPermission(
        RBACService.RBAC_F.F_EMPLOYEES,
        RBACService.RBAC_A.A_DELETE
      )
    ]);
    if (!hasAnyPermission) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to access employees.'
      );
      this.router.navigate(['/employees']);
      return;
    }

    // Get the employee id
    const employeeId = Number(this.route.snapshot.params['user_id']);
    // Check if it it a valid employee id
    if (!employeeId) {
      this.router.navigate(['employees']);
      return;
    }

    this.employeeService.shouldRedirectToEmployeesOnFailure = true;

    // Gate employee data fetch behind view or edit permission.
    if (this.canViewOrEditEmployee) {
      this.employeeService.getEmployeeById(employeeId);
    }
  }

  // This poses a massive security risk without account verification added.
  onSendPasswordResetEmail() {
    // Partner users are managed by support. In place for when this action is re-enabled.
    if (this.rbac.partnerLockApplies(this.employeeService.employee.is_partner_managed)) {
      this.rbac.showGenericAccessDeniedMessage(this.rbac.partnerManagedMessage());
      return;
    }
    return;
    this.utils.showModal(
      'Confirmation Needed',
      'Are you sure you want to send the password reset instructions to ' +
        this.employeeService.employee.email +
        '?',
      () => {
        this.employeeService.sendPasswordResetEmail();
      }
    );
  }

  composeNewMessage(employeeId: number) {
    if (!employeeId) {
      this.utils.showToast(
        'Unable to open message composer. Please try again.'
      );
      return; // early termination
    }

    this.utils
      .showComponentDialog(
        MessageEditComponent,
        {
          preSelectUserIds: [employeeId]
        },
        {
          width: '1200px'
        }
      )
      .then((response) => {
        if (response) {
          this.utils.showToast(
            'You can view the message stats in the messages section.'
          );
        }
      });
  }

  onUnlockUser(employeeId: number) {
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_EMPLOYEES,
            RBACService.RBAC_A.A_EDIT
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to edit this employee.'
      );
      return;
    }
    this.employeeService.unlockEmployeeForLogin(employeeId);
  }

  // Expose RBACService and AppService to template
  protected readonly RBACService = RBACService;
  protected readonly AppService = AppService;
}
